import { Button, Stack, Typography } from "@mui/material"
import { Frame } from "../../components/layouts/Frame"
import { DataTable } from "../../components/global/Table/DataTable"
import { ActiveIcon } from "../../assets/svgs/ActiveIcon";
import { Link } from "../../components/global/Link";
import { EditIcon } from "../../assets/svgs/EditIcon";
import { DeactiveIcon } from "../../assets/svgs/DeactiveIcon";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AddClient } from "./AddClient";
import { EditClient } from "./EditClient";
import { DataTableHeadCellData } from "../../components/global/Table/DataTableHeader";
import { ClientIcon } from '../../assets/svgs/ClientIcon';
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../assets/contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";
import { ClientData, SectorData } from "../../types";

const headCells: DataTableHeadCellData[] = [
  {
    id: 'name',
    disablePadding: true,
    label: 'Name',
    align: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'sector',
    disablePadding: true,
    label: 'Sector',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'active',
    disablePadding: false,
    label: 'Active',
    width: '50px',
  },
  {
    id: 'edit',
    disablePadding: false,
    label: 'Edit',
    width: '50px',
  },
];

interface ClientTableData {
  id: any;
  name: any;
  sector: any;
  active: JSX.Element;
  edit: JSX.Element;
}

export const Clients = () => {
  const navigate = useNavigate();
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { fetchData } = useAPI();
  const [rows, setRows] = useState<ClientTableData[]>([]);
  const [sectors, setSectors] = useState<SectorData[]>([]);
  const [clients, setClients] = useState<ClientData[]>([]);

  const onAddClient = () => {
    navigate('/clients/create');
  }

  const onBulkDelete = async (ids: number[]) => {
    await fetchData('/clients/delete',  { id: ids }, 'POST');
    refetchClients();
  }

  const refetchClients = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const {errors, data} = await fetchData('/clients/all');
    setClients(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  }

  const refetchSectors = async () => {
    const {errors, data} = await fetchData('/sectors/all');
    setSectors(data ?? []);
  }

  const getRows = (clients: ClientData[]) => {
    const result: ClientTableData[] = [];
    for (let i=0; i<clients.length; i++) {
      const sector = sectors.find((item) => item.id == clients[i].sector_id);
      result.push({
        id: `${clients[i].id}`,
        name: <Typography>{clients[i].name}</Typography>,
        sector: <Typography>{sector?.name ?? ''}</Typography>,
        active: clients[i].status ? <ActiveIcon /> : <DeactiveIcon />,
        edit: <Link to={`/clients/${clients[i].id}`}><EditIcon /></Link>
      });
    }
    return result;
  }

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchClients();
      refetchSectors();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setRows(getRows(clients));
  }, [clients, sectors]);

  return (
    <Frame title="Clients" toolbarButtons={(
      <>
        <Button variant="contained" onClick={onAddClient} startIcon={<ClientIcon width="22" height="15" color="#FFFFFF" />}>
          Add Client
        </Button>
      </>
    )}>
      <Stack>
        <Routes>
          <Route
            path="/create/"
            element={<AddClient refetchClients={refetchClients} sectors={sectors} />}
          ></Route>
          <Route
            path="/:client_id/"
            element={<EditClient refetchClients={refetchClients} sectors={sectors} />}
          ></Route>
        </Routes>
        <DataTable 
          title="Clients" 
          headCells={headCells} 
          rows={rows} 
          isLoading={sIsLoading} 
          onBulkDelete={onBulkDelete} 
          hideIdCheckbox={true}
          hideDeleteButton={true}
        />
      </Stack>
    </Frame>
  )
}
